import { subDays } from "date-fns"
import { min } from "lodash"

import { formatDate, startOfTodayUTC } from "~/utils/datetime"
import { isAllFashion } from "~/utils/productTypeUtils"

import type { IVariantSummary } from "~/hooks/useVariantTableData"
import { Badge, BadgeVariant } from "~/components/reusable/Badge"

interface ReorderDates {
  lastReorderDate: Date | undefined
  reorderDate: Date | undefined
}

enum ReorderUrgencyStatus {
  None = "none",
  Urgent = "urgent",
  Warning = "warning",
  Safe = "safe",
}

const statusConfig: Record<ReorderUrgencyStatus, { variant: BadgeVariant; text: string }> = {
  [ReorderUrgencyStatus.None]: {
    variant: "white",
    text: "-",
  },
  [ReorderUrgencyStatus.Urgent]: {
    variant: "emphasizedRed",
    text: "Order now",
  },
  [ReorderUrgencyStatus.Warning]: {
    variant: "emphasizedOrange",
    text: "Order now",
  },
  [ReorderUrgencyStatus.Safe]: {
    variant: "emphasizedGreen",
    text: "",
  },
}

function calculateReorderDates(entries: IVariantSummary[]): ReorderDates {
  if (entries.length === 0) {
    return { lastReorderDate: undefined, reorderDate: undefined }
  }

  if (isAllFashion(entries)) {
    return { lastReorderDate: undefined, reorderDate: undefined }
  }

  const relevantInfo = entries.flatMap((entry) =>
    Object.values(entry.stockOutInfoWarehouse ?? {})
      .flatMap((s) => s)
      .filter((o) => (o.suggestedOrder ?? 0) > 0)
      .map((o) => ({ o, totalLeadTimeDays: entry.settings.totalLeadTimeDays })),
  )

  const lastReorderDate = min(
    relevantInfo.map(({ o, totalLeadTimeDays }) =>
      o.stockoutDates[0] ? subDays(o.stockoutDates[0], totalLeadTimeDays) : undefined,
    ),
  )

  const reorderDate = min(
    relevantInfo.map(({ o, totalLeadTimeDays }) =>
      o.safetyStockOutDate ? subDays(o.safetyStockOutDate, totalLeadTimeDays) : undefined,
    ),
  )

  return { lastReorderDate, reorderDate }
}

function filterEntriesWithSuggestedOrder(entries: IVariantSummary[]): IVariantSummary[] {
  return entries.filter((entry) => entry.suggestedOrder && entry.suggestedOrder > 0)
}

export function optimalOrderDateTooltipText(entries: IVariantSummary[]) {
  const relevantEntries = filterEntriesWithSuggestedOrder(entries)
  const { lastReorderDate, reorderDate } = calculateReorderDates(relevantEntries)
  const multipleEntriesText = relevantEntries.length > 1 ? "for at least one SKU" : ""

  if (!reorderDate) return "-"

  if (lastReorderDate && lastReorderDate < startOfTodayUTC()) {
    return `You will be out of stock ${multipleEntriesText}, before you can receive new items.`
  } else if (reorderDate < startOfTodayUTC()) {
    return `Order now to avoid using the safety stock, ${multipleEntriesText}.`
  } else {
    return `Order by ${formatDate(reorderDate)} to avoid using the safety stock, ${multipleEntriesText}.`
  }
}

function getReorderUrgencyStatus(entry: IVariantSummary): ReorderUrgencyStatus {
  const { lastReorderDate, reorderDate } = calculateReorderDates([entry])
  if (!reorderDate) return ReorderUrgencyStatus.None
  if (lastReorderDate && lastReorderDate < startOfTodayUTC()) return ReorderUrgencyStatus.Urgent
  if (reorderDate < startOfTodayUTC()) return ReorderUrgencyStatus.Warning
  return ReorderUrgencyStatus.Safe
}

function findHighestPriorityStatus(statuses: ReorderUrgencyStatus[]): ReorderUrgencyStatus {
  if (statuses.includes(ReorderUrgencyStatus.Urgent)) return ReorderUrgencyStatus.Urgent
  if (statuses.includes(ReorderUrgencyStatus.Warning)) return ReorderUrgencyStatus.Warning
  if (statuses.includes(ReorderUrgencyStatus.Safe)) return ReorderUrgencyStatus.Safe
  return ReorderUrgencyStatus.None
}

export function OptimalOrderDateFieldV2({ entries }: { entries: IVariantSummary[] }) {
  const relevantEntries = filterEntriesWithSuggestedOrder(entries)
  const { reorderDate } = calculateReorderDates(relevantEntries)
  if (!reorderDate) return "-"
  const statuses = relevantEntries.map(getReorderUrgencyStatus)
  const status = findHighestPriorityStatus(statuses)

  const { variant, text } = statusConfig[status]

  return (
    <Badge variant={variant}>
      {status === ReorderUrgencyStatus.Safe ? formatDate(reorderDate) : text}
    </Badge>
  )
}
